import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import useAwsPartnerCentral from '@/composables/useAwsPartnerCentral';
import usePolling from '@/composables/usePolling';
import {
  DELETE_FEED_INTERVAL,
  DELETING_FEED_STATUS,
} from '@/constants/data_sources';
import { captureException } from '@/errors';
import { useFeedsStore, useFlashesStore } from '@/stores';

export default function useFeedsDeleting() {
  const flashesStore = useFlashesStore();
  const feedsStore = useFeedsStore();
  // AWS Partner Central feeds live outside FeedsStore, so include them here and
  // refresh them alongside so a deleting AWS feed is tracked and clears.
  const { awsFeeds, refreshAwsPartnerCentral } = useAwsPartnerCentral();

  const { feeds } = storeToRefs(feedsStore);
  const deletingFeeds = computed(() =>
    [...feeds.value, ...awsFeeds.value].filter(
      (f) => f.status === DELETING_FEED_STATUS,
    ),
  );
  const feedsAreDeleting = computed(() => deletingFeeds.value.length > 0);

  const isPolling = ref(false);

  let originalDeletingFeeds =
    deletingFeeds.value.length; /* Non reactive value */
  const { start: poll } = usePolling({
    interval: DELETE_FEED_INTERVAL,
    async poller() {
      // refreshAwsPartnerCentral throws on failure. Surface that (stop polling +
      // error flash) only when an AWS feed is actually deleting; otherwise a
      // transient AWS error must not abort polling for non-AWS deletes.
      const awsFeedDeleting = awsFeeds.value.some(
        (feed) => feed.status === DELETING_FEED_STATUS,
      );
      await Promise.all([
        feedsStore.refreshFeedsStore(),
        awsFeedDeleting
          ? refreshAwsPartnerCentral()
          : refreshAwsPartnerCentral().catch(captureException),
      ]);
    },
    shouldComplete() {
      if (deletingFeeds.value.length < originalDeletingFeeds)
        flashesStore.addSuccessFlash({
          message: 'Data source deleted successfully',
        });
      if (!feedsAreDeleting.value) {
        isPolling.value = false;
        return true;
      }
    },
    onError(err) {
      captureException(err);
      flashesStore.addErrorFlash({
        message: 'There was a problem deleting your data source',
      });
      isPolling.value = false;
    },
  });

  if (feedsAreDeleting.value) {
    isPolling.value = true;
    poll();
  }

  watch(feedsAreDeleting, (newValue, oldValue) => {
    if (newValue && !oldValue && !isPolling.value) {
      originalDeletingFeeds = deletingFeeds.value.length;
      isPolling.value = true;
      poll();
    }
  });
}
